/**
 * @license
 * Copyright Akveo. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */
import { Inject, Injectable } from '@angular/core';

import { Observable, of as observableOf, Subject  } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';

import { NbAuthStrategy } from '../strategies/auth-strategy';
import { NB_AUTH_STRATEGIES } from '../auth.options';
import { NbAuthResult } from './auth-result';
import { NbTokenService } from './token/token.service';
import { NbAuthJWTToken, NbAuthToken } from './token/token';
import axios, {AxiosError, AxiosResponse} from 'axios';
import { environment } from '../../../environments/environment';
import { twoFAresponse } from '../../interfaces/apiCalls';
import { loginPagePath } from '../../services/pagePaths';
import { ApiService } from '../../services/api/api.service';
import { RegisterOrLoginWithP12, UserInvite } from '@med_poc/types';
import { Router } from '@angular/router';
import { cloneDeepWith } from 'lodash-es';

/**
 * Common authentication service.
 * Should be used to as an interlayer between UI Components and Auth Strategy.
 */
@Injectable()
export class NbAuthService {
  getRoleTrigger: Subject<any>; // due to circular dependency issues, we cannot import the API service here. So we pass an event emitter here from the API service which will be used to notify the API service to refetch the user role.
  // create event emitter for an array of strings, that will be the roles of the user.
  // this will be used to notify other parts of the app about role changes.
  // roles$: EventEmitter<string[]> = new EventEmitter<string[]>();
  private apiUrl: string;

  private readonly trimExcludedKeyPattern = /(pass(word)?|token|secret|api[-_]?key|authorization|auth)/i;

  private shouldSkipStringTrim(key: unknown): boolean {
    return typeof key === 'string' && this.trimExcludedKeyPattern.test(key);
  }

  private deepTrimStrings<T>(value: T): T {
    return cloneDeepWith(value, (currentValue, key) => {
      if (typeof currentValue !== 'string') return undefined;
      if (this.shouldSkipStringTrim(key)) return currentValue;
      return currentValue.trim();
    },
    ) as T;
  }

  constructor(protected tokenService: NbTokenService,
              @Inject(NB_AUTH_STRATEGIES) protected strategies,
              public router: Router) {

       this.apiUrl = environment.apiUrl;
  }

  /**
   * Retrieves current authenticated token stored
   * @returns {Observable<any>}
   */
  getToken(): Observable<NbAuthToken> {
    return this.tokenService.get();
  }

  /**
   * Returns true if auth token is present in the token storage
   * @returns {Observable<boolean>}
   */
  isAuthenticated(): Observable<boolean> {
    return this.getToken()
      .pipe(map((token: NbAuthToken) => token.isValid()));
  }

  /**
   * Returns true if valid auth token is present in the token storage.
   * If not, calls the strategy refreshToken, and returns isAuthenticated() if success, false otherwise
   * @returns {Observable<boolean>}
   */
  isAuthenticatedOrRefresh(): Observable<boolean> {
    return this.getToken()
      .pipe(
        switchMap(token => {
        if (token.getValue() && !token.isValid()) {

          // redirect to login page
          this.router.navigate([loginPagePath()]);

          /* return this.refreshToken(token.getOwnerStrategyName(), token)
            .pipe(
              switchMap(res => {
                if (res.isSuccess()) {
                  return this.isAuthenticated();
                } else {
                  return observableOf(false);
                }
              }),
            ) */
            return observableOf(false);
        } else {
          return observableOf(token.isValid());
        }
    }));
  }
  
  /**
   * Returns tokens stream
   * @returns {Observable<NbAuthSimpleToken>}
   */
  onTokenChange(): Observable<NbAuthToken> {
    return this.tokenService.tokenChange();
  }

  /**
   * Returns authentication status stream
   * @returns {Observable<boolean>}
   */
  onAuthenticationChange(): Observable<boolean> {
    return this.onTokenChange()
      .pipe(map((token: NbAuthToken) => token.isValid()));
  }

  /**
   * Makes backend send user a twoFA token.
   *
   * Example:
   * authenticate('email', {email: 'email@example.com', password: 'test'})
   *
   * @param strategyName
   * @param data
   * @returns {Observable<NbAuthResult>}
   */
  sendTwoFA(strategyName: string, credentials: {email: string, password:string, generateLoginToken?: string }) : Promise<twoFAresponse> {
    return new Promise((resolve, reject) => {
      const normalizedCredentials = this.deepTrimStrings(credentials);
      var data 
      
      //gateway scenario
      if(environment.childOrMasterOrGateway == 'gateway'){
        data = JSON.stringify({
        "identifier": normalizedCredentials.email,
        "password": normalizedCredentials.password,
        "generateLoginToken": normalizedCredentials.generateLoginToken
      });
    } else {

      data = JSON.stringify({
        "identifier": normalizedCredentials.email,
        "password": normalizedCredentials.password,
      });
    }
      var config;
    // gateway scenario
    if(environment.childOrMasterOrGateway == 'gateway') {

      config = {
        method: 'post',
        url: `${this.apiUrl}api/clients/login/`,
        headers: { 
          'Content-Type': 'application/json', 
          'Accept': 'application/json'
        },
        data : data
      };
    } else{
      config = {
        method: 'post',
        url: `${this.apiUrl}api/auth/local`,
        headers: { 
          'Content-Type': 'application/json', 
          'Accept': 'application/json'
        },
        data : data
      };
    }

      axios(config)
      .then(function (response : AxiosResponse) {
        console.devlog(JSON.stringify(response.status));
        if(response.status == 202) {
          resolve({success: true, reason: undefined})
          console.devlog(JSON.stringify(response.data));
        }else if(response.status == 200) {
          // in case we got everything we need:
          resolve({success: true, reason: undefined, response: response})
          console.devlog("twoFA response:", {success: true, reason: undefined, response: response});
        } 
        else {
          resolve({success: false, reason: 'BAD_REQUEST', response: response})
        } 
      })
      .catch(function (error) {
        console.devlog("twoFA error:", error)
        resolve({success: false, reason: undefined, response: error.response})
      });
      
    })
  }
  /**
   * Authenticates with the selected strategy
   * Stores received token in the token storage
   *
   * Example:
   * authenticate('email', {email: 'email@example.com', password: 'test'})
   *
   * @param strategyName
   * @param data
   * @returns {Observable<NbAuthResult>}
   */

    /**
   * Make the client send you a twoFA token,
   * providing your credentials, the client code, the preconfigured 'generateLoginToken' flag.
   *
   * Example:
   * getOneTimeLoginToken( 'foo@bar.com', 'password', 'ASDF')
   *
   * @param identifier
   * @param password
   * @param clientCode
   * @returns {Promise<twoFAresponse> }
   */
  async makeClientSendTwoFA(identifier: string, password: string, clientCode: string) : Promise<twoFAresponse> {
    return new Promise((resolve, reject) => {
        const normalizedInput = this.deepTrimStrings({
          "identifier": identifier,
          "password": password,
          "clientCode": clientCode,
          "generateLoginToken": true,
        });
      
        let data = JSON.stringify(normalizedInput);
        
        let config = {
          method: 'post',
          maxBodyLength: Infinity,
          url: `${this.apiUrl}api/clients/login/`,
          headers: { 
            'Content-Type': 'application/json'
          },
          data : data
        };
        
        axios.request(config)
        .then((response : AxiosResponse) => {
          // in case we got everything we need:
          console.devlog("makeClientSendTwoFA response:", {success: true, reason: undefined, response: response});
          if(response.status.toString().startsWith("2")) {
            resolve({success: true, reason: undefined, response: response})
            
            console.devlog(JSON.stringify(response.data));
          }
        })
        .catch((error) => {
          console.devlog(error);
          reject(error);
        });
    })
};
    /**
   * Receive the oneTimeLoginToken from the client,
   * providing the previously acquired twoFA token and your credentials.
   *
   * Example:
   * getOneTimeLoginToken( 'foo@bar.com', 'password', 'ASDF', '123456')
   *
   * @param identifier
   * @param password
   * @param clientCode
   * @param twoFAtoken
   * @returns {Promise<{oneTimeLoginToken: string, redirectTo: string}> }
   */
  async getOneTimeLoginToken(identifier: string, password: string, clientCode: string, twoFAtoken?: string) : Promise<{oneTimeLoginToken: string, redirectTo: string}> {
    return new Promise((resolve, reject) => {
        const normalizedInput = this.deepTrimStrings({
          "identifier": identifier,
          "password": password,
          "clientCode": clientCode,
          "generateLoginToken": true,
          "twoFAtoken": twoFAtoken
        });
     
        let data = JSON.stringify(normalizedInput);
        
        let config = {
          method: 'post',
          maxBodyLength: Infinity,
          url: `${this.apiUrl}api/clients/login/`,
          headers: { 
            'Content-Type': 'application/json'
          },
          data : data
        };
        
        axios.request(config)
        .then((response : AxiosResponse) => {

          console.devlog("oneTimeLoginToken response status:", JSON.stringify(response.status));
          console.devlog("oneTimeLoginToken response data:", JSON.stringify(response.data), );
          // in case we got everything we need:
          if(response.status.toString().startsWith("2") && response.data['oneTimeLoginToken'] && response.data['redirectTo']) {

            // replace 127.0.0.1 to localhost as chrome is for some reason refusing it !
            if(response.data['redirectTo'].includes('127.0.0.1')) {
              response.data['redirectTo'] = (response.data['redirectTo'] as string ).replace('127.0.0.1', 'localhost')
            }

            resolve({oneTimeLoginToken: response.data.oneTimeLoginToken, redirectTo: response.data.redirectTo})
            console.devlog(JSON.stringify(response.data));
          }
        })
        .catch((error) => {
          console.devlog(error);
          reject(error);
        });
    })
};

  authenticate(strategyName: string, apiServiceRef: ApiService, data?: any): Observable<NbAuthResult> {

    const normalizedData = this.deepTrimStrings(data);

    const strategy : NbAuthStrategy = this.getStrategy(strategyName);

    // strategy.
    return this.getStrategy(strategyName).authenticate(normalizedData)
      .pipe(
        switchMap((result: NbAuthResult) => {
          return this.processResultToken(result, apiServiceRef);
        }),
      );
  }

  /**
   * Registers with the selected strategy
   * Stores received token in the token storage
   *
   * Example:
   * register('email', {email: 'email@example.com', name: 'Some Name', password: 'test'})
   *
   * @param strategyName
   * @param data
   * @returns {Observable<NbAuthResult>}
   */
  register(strategyName: string, apiServiceRef: ApiService, data?: any): Observable<NbAuthResult> {
    const normalizedData = this.deepTrimStrings(data);
    return this.getStrategy(strategyName).register(normalizedData)
      .pipe(
        switchMap((result: NbAuthResult) => {
          return this.processResultToken(result, apiServiceRef);
        }),
      );
  }

  /**
   * Sign outs with the selected strategy
   * Removes token from the token storage
   *
   * Example:
   * logout('email')
   *
   * @param strategyName
   * @returns {Observable<NbAuthResult>}
   */
  logout(strategyName: string): Observable<NbAuthResult> {
    return this.getStrategy(strategyName).logout()
      .pipe(
        switchMap((result: NbAuthResult) => {
          if (result.isSuccess()) {
            this.tokenService.clear()
              .pipe(map(() => result));
          }
          return observableOf(result);
        }),
      );
  }

  /**
   * Sends forgot password request to the selected strategy
   *
   * Example:
   * requestPassword('email', {email: 'email@example.com'})
   *
   * @param strategyName
   * @param data
   * @returns {Observable<NbAuthResult>}
   */
  requestPassword(strategyName: string, data?: any): Observable<NbAuthResult> {
    return this.getStrategy(strategyName).requestPassword(data);
  }

  /**
   * Tries to reset password with the selected strategy
   *
   * Example:
   * resetPassword('email', {newPassword: 'test'})
   *
   * @param strategyName
   * @param data
   * @returns {Observable<NbAuthResult>}
   */
  resetPassword(strategyName: string, data?: any): Observable<NbAuthResult> {
    return this.getStrategy(strategyName).resetPassword(data);
  }

  /**
   * Sends a refresh token request
   * Stores received token in the token storage
   *
   * Example:
   * refreshToken('email', {token: token})
   *
   * @param {string} strategyName
   * @param data
   * @returns {Observable<NbAuthResult>}
   */
  refreshToken(strategyName: string, apiServiceRef: ApiService, data?: any): Observable<NbAuthResult> {
    return this.getStrategy(strategyName).refreshToken(data)
      .pipe(
        switchMap((result: NbAuthResult) => {
          return this.processResultToken(result, apiServiceRef);
        }),
      );
  }

  /**
   * Get registered strategy by name
   *
   * Example:
   * getStrategy('email')
   *
   * @param {string} provider
   * @returns {NbAbstractAuthProvider}
   */
  protected getStrategy(strategyName: string): NbAuthStrategy {
    const found = this.strategies.find((strategy: NbAuthStrategy) => strategy.getName() === strategyName);

    if (!found) {
      throw new TypeError(`There is no Auth Strategy registered under '${strategyName}' name`);
    }

    return found;
  }



  processResultToken(result: NbAuthResult, apiServiceRef: ApiService, opts?: ProcessResultTokenOpt): Observable<NbAuthResult> {
    if (result.isSuccess() && result.getToken()) {
      return this.tokenService.set(result.getToken())
        .pipe(
          map((token: NbAuthToken) => {
            try {



              // make it wait by resolving a promise a little later
              //new Promise(resolve => setTimeout(resolve, 60));

              this.runAfterLogin(apiServiceRef, opts).then(() => {

              });
            } catch (e) {
              console.devwarn("Error in the runAfterLogin:", e);
            }
            return result;
          }),
        );
    }

    return observableOf(result);
  }

    runAfterLogin = async (apiServiceRef: ApiService, opts?: ProcessResultTokenOpt) =>  {

      await new Promise(resolve => setTimeout(resolve, 160));
      // fetch user data from backend and store it in the api service 
      await apiServiceRef.setMe({overridePublicRoutesCheck: true});

      // apply user's saved language preference 
      const user = apiServiceRef.state.getUser();
      if (user?.language) {
        apiServiceRef.state.setLanguage(user.language, {silent: true})
        // localStorage.setItem('language', user.language);
      }

      // fetch all the roles
      await apiServiceRef.state.loadRoles(true)

      // fetch professional roles from backend and store them in the api service
      console.devlog("Fetching professional roles after login...");
      //@ts-ignore
      apiServiceRef.state.professionalRoles = await apiServiceRef.getAllProfessionalRoles()

      console.devlog("Fetched professional roles:", apiServiceRef.state.professionalRoles);

      // fetch user roles from backend and store them in the api service
      // perform redirects etc

      if(opts?.overrideRedirectAfterLogin) {
              setTimeout(() => {
              console.devlog("redirecting to overriden redirectToAfterLogin:", opts.overrideRedirectAfterLogin);
              this.router.navigateByUrl(opts.overrideRedirectAfterLogin);
              return;
            }, opts.redirectDelay ?? 1);
          }  else {
             console.devlog("redirecting to defined redirectToAfterLogin:", localStorage.getItem('redirectToAfterLogin') ?? apiServiceRef.state.defaultRedirectToAfterLogin);

             this.router.navigateByUrl(localStorage.getItem('redirectToAfterLogin') ?? apiServiceRef.state.defaultRedirectToAfterLogin);
             localStorage.removeItem('redirectToAfterLogin');
             return;

        }

    }
  
  // a ref to the API service is passed here, because direct imoprt of API here would result in a circular dependency.
  async registerOrLoginWithCertificate(cnsCode, certPassword, userPassword, certFileRef, apiServiceRef : ApiService , overrideRedirectAfterLogin? : `/${string}`, redirectDelay?) : Promise<RegisterOrLoginWithP12["ResponseData"]["BadRequest"] | RegisterOrLoginWithP12["ResponseData"]["Error"] | true> {
        let response;
        try{
          response = await apiServiceRef.RegisterOrLoginWithP12(cnsCode , certPassword, userPassword, certFileRef)
          const data = response.data;
        // console.log('Result of registerOrLoginWithCertificate:', data);
  
        if ((data as RegisterOrLoginWithP12["ResponseData"]["Success"]).jwt) {
          let fakeResponse = {
            body: {
              jwt: (data as RegisterOrLoginWithP12["ResponseData"]["Success"]).jwt,
              user: (data as RegisterOrLoginWithP12["ResponseData"]["Success"]).user
            },
            headers: {
              /* normalizedNames: new Map(),
              lazyUpdate: null,
              lazyInit: () => {} */
            },
            ok: true,
            status: 200,
            statusText: "OK",
            type: 4,
            url: `${this.apiUrl}api/auth/local`
          }
  
          const myToken = new NbAuthJWTToken((data as RegisterOrLoginWithP12["ResponseData"]["Success"]).jwt, 'email', new Date());
  
          // construct a fake NbAuthResult with the JWT we received and set it in the frontend.
          let fakeNbAuthResult: NbAuthResult = new NbAuthResult(true, fakeResponse, "/", null, ['You have been successfully logged in.'], myToken)
      
          let options: ProcessResultTokenOpt = {
            overrideRedirectAfterLogin: overrideRedirectAfterLogin,
            redirectDelay: redirectDelay
          }
         let loginResult = this.processResultToken(fakeNbAuthResult, apiServiceRef, options).toPromise()
          return true;
      
        } /* else {
          // this else block is probably unneeded, as all error cases should be handled in the catch block below.

          // check if the request was an AxiosError and return proper axios error here !
          console.log("registerOrLoginWithCertificate: no JWT received, something went wrong.", data);
          throw new Error("Something went wrong with the login, no JWT received.");
        } */

      } catch (error) {
          // here we deal with an axios error
          return error;
      }
  }
  async loginWithInviteCode(code: string, password: string, birthDate: string, apiServiceRef : ApiService , overrideRedirectAfterLogin? : `/${string}`, redirectDelay?) : Promise<UserInvite["acceptUserInviteWithCode"]["ResponseData"]['BadRequest'] | UserInvite["acceptUserInviteWithCode"]["ResponseData"]['Error'] | UserInvite["acceptUserInviteWithCode"]["ResponseData"]['Success']> {
        let response;
        try{
          response = await apiServiceRef.acceptInviteUserWithCode(code, password, birthDate)
          const data = response.data;
        // console.log('Result of registerOrLoginWithCertificate:', data);
  
        if ((data as RegisterOrLoginWithP12["ResponseData"]["Success"]).jwt) {
          // constructing this object to satisfy NbAuthResult constructor
          let fakeResponse = {
            body: {
              jwt: (data as RegisterOrLoginWithP12["ResponseData"]["Success"]).jwt,
              user: (data as RegisterOrLoginWithP12["ResponseData"]["Success"]).user
            },
            headers: {
              /* normalizedNames: new Map(),
              lazyUpdate: null,
              lazyInit: () => {} */
            },
            ok: true,
            status: 200,
            statusText: "OK",
            type: 4,
            url: `${this.apiUrl}api/auth/local`
          }
  
          const myToken = new NbAuthJWTToken((data as RegisterOrLoginWithP12["ResponseData"]["Success"]).jwt, 'email', new Date());
  
          // construct a fake NbAuthResult with the JWT we received and set it in the frontend.
          let fakeNbAuthResult: NbAuthResult = new NbAuthResult(true, fakeResponse, "/", null, ['You have been successfully logged in.'], myToken)
      
          let options: ProcessResultTokenOpt = {
            overrideRedirectAfterLogin: overrideRedirectAfterLogin,
            redirectDelay: redirectDelay
          }

         let loginResult = await this.processResultToken(fakeNbAuthResult, apiServiceRef, options).toPromise()
          return data;

        } else if ((data as UserInvite["acceptUserInviteWithCode"]["ResponseData"]["BadRequest"]).message && (data as UserInvite["acceptUserInviteWithCode"]["ResponseData"]["BadRequest"]).status === 400) {
          // handle specific error case
          console.deverror('Error accepting user invite:', (data as UserInvite["acceptUserInviteWithCode"]["ResponseData"]["BadRequest"]).message);
          return (data as UserInvite["acceptUserInviteWithCode"]["ResponseData"]["BadRequest"]);

        } /* else {
          // this else block is probably unneeded, as all error cases should be handled in the catch block below.

          // check if the request was an AxiosError and return proper axios error here !
          console.log("registerOrLoginWithCertificate: no JWT received, something went wrong.", data);
          throw new Error("Something went wrong with the login, no JWT received.");
        } */

      } catch (error) {
          // here we deal with an axios error
          if (axios.isAxiosError(error)) {
            const axiosError = error as AxiosError;
            if (axiosError.response) {
              console.deverror('Axios error response data:', axiosError.response.data);
              return axiosError.response.data as UserInvite["acceptUserInviteWithCode"]["ResponseData"]["Error"];
            }
          } else {
            console.devlog("loginWithInviteCode: non-axios error:", error);
            return error;
          }
      }
  }

  recieveGetRoleTrigger(getRoleTrigger: Subject<any>) {
    this.getRoleTrigger = getRoleTrigger;
  }
}

 export type ProcessResultTokenOpt = {
  overrideRedirectAfterLogin?: `/${string}`,
  redirectDelay?: number,
}
